import { CarbonUser } from "common/utils/auth";
import { HitchType, PtoType } from "protos/atk/enums";
import { Duration as LuxonDuration } from "luxon";
import {
  luxonDurationToProtobuf,
  protobufDurationToLuxon,
} from "common/utils/duration";
import { MuiIcon } from "common/components/types";
import { Duration as ProtobufDuration } from "protos/google/protobuf/duration";
import { QuickAction } from "protos/atk/activities";
import { RigEffectiveConfig } from "protos/atk/effective_config";
import { TFunction } from "i18next";
import { TypedPathTo } from "portal/utils/typedPath";
import { v4 as uuidv4 } from "uuid";
import React from "react";

export type Effector =
  | { type: "engine-rpm" }
  | { type: "advanced" }
  | { type: "pto"; ptoType: PtoType }
  | { type: "hitch"; hitchType: HitchType }
  | { type: "scv"; scvInputId: string }
  | { type: "laserweeder"; implementId: string }
  | { type: "lw-estop"; implementId: string };

export function effectorToMapKey(effector: Effector): string {
  switch (effector.type) {
    case "pto": {
      return `pto|${effector.ptoType}`;
    }
    case "hitch": {
      return `hitch|${effector.hitchType}`;
    }
    case "scv": {
      return `scv|${effector.scvInputId}`;
    }
    case "laserweeder": {
      return `lw|${effector.implementId}`;
    }
    case "lw-estop": {
      return `lw-estop|${effector.implementId}`;
    }
    default: {
      return effector.type;
    }
  }
}

export abstract class ActionViewModel<TImplementation> {
  protected constructor(
    public readonly effector: Effector,
    public readonly holdingAction: boolean,
    public startInStageIndex: number,
    public startDelaySec: number
  ) {}

  /**
   * A method for this view model which returns whether or not a given user can
   * edit it.  Usually true, except for advanced actions which can only be
   * edited by Carbon employees.
   * @param _user the user object of the current user
   * @returns true if the user can edit this action (subject to global editing
   *   privileges for the rig configuration), false otherwise
   */
  canEdit(_user: CarbonUser | undefined): boolean {
    return true;
  }

  /**
   * A method for this view model which returns the localized string that should
   * be displayed on the new action menu if an effector cannot be selected
   * because this action is already scheduled for it.
   * @param t an i18next `t` function
   * @returns the localized string to display on the new action toplevel menu
   */
  abstract effectorBlockedByActionMessage(t: TFunction): string;

  /**
   * A method for this view model which converts it back to the underlying
   * configuration object.
   * @param finalActionThisEffector true if this action is the final action
   *   scheduled for its effector in this action sequence
   */
  abstract toQuickAction(finalActionThisEffector: boolean): QuickAction;

  /**
   * A method for this view model which returns the localized string that should
   * be displayed on its action card.  (Note: this method is also used to
   * determine whether a given action should be rendered in a given stage index:
   * if it returns undefined, the action card for this action is not rendered
   * in the stage at all.)
   * @param t an i18next `t` function
   * @param stageIndex the index of the stage in which the action is being rendered
   * @param finalActionThisEffector true if this action is the final action
   *   scheduled for its effector in this action sequence
   */
  abstract actionCardTitle(
    t: TFunction,
    rigEffectiveConfig: RigEffectiveConfig,
    stageIndex: number,
    finalActionThisEffector: boolean
  ): string | undefined;

  abstract components: ActionViewModelImplementation<TImplementation>["components"];
}

export type ActionMenuSection = "quick-action" | "holding-action";

export interface ActionViewModelImplementation<TViewModel> {
  actionButtonLabelI18nKey: string;
  actionMenuSection: ActionMenuSection;
  isNewActionAvailableForEffector: (
    user: CarbonUser | undefined,
    effector: Effector,
    rigEffectiveConfig: RigEffectiveConfig
  ) => boolean;
  newAction: (
    effector: Effector,
    startInStageIndex: number
  ) => ActionViewModel<TViewModel>;
  fromQuickAction: (
    action: QuickAction,
    startInStageIndex: number
  ) => ActionViewModel<TViewModel> | undefined;
  components: {
    Icon: MuiIcon;
    EditForm: React.FC<{ basePath: TypedPathTo<TViewModel> }>;
  };
}

export function durationInSecondsOrZero(
  duration: ProtobufDuration | undefined
): number {
  return protobufDurationToLuxon(duration ?? { seconds: 0, nanos: 0 }).as(
    "seconds"
  );
}
export function secondsToDuration(durationSec: number): ProtobufDuration {
  return luxonDurationToProtobuf(
    LuxonDuration.fromObject({ seconds: durationSec })
  );
}

export function uuidOrNewUuid(id: string | undefined): string {
  return id !== undefined && id !== "" ? id : uuidv4();
}
